/**
 * API service for communicating with the Django backend
 * Supports multi-tenant architecture via domain-based identification
 */

import { authService } from './authService';

export interface ApiRequestOptions extends Omit<RequestInit, 'body' | 'headers' | 'method'> {
  body?: BodyInit | null;
  headers?: HeadersInit;
  method?: string;
  raw?: boolean;
  responseType?: 'arraybuffer' | 'blob' | 'text';
  /** With responseType: 'blob', resolve to { blob, filename } instead of a bare Blob, reading `filename` off the response's Content-Disposition header. */
  withFilename?: boolean;
}

export interface BlobWithFilename {
  blob: Blob;
  filename: string | null;
}

/** Parses the filename out of a `Content-Disposition: attachment; filename="foo.pdf"` (or `filename*=UTF-8''foo.pdf`) header value. */
const parseContentDispositionFilename = (headerValue: string | null): string | null => {
  if (!headerValue) return null;
  const starMatch = headerValue.match(/filename\*=(?:[^']*'')?([^;]+)/i);
  if (starMatch) {
    try { return decodeURIComponent(starMatch[1].trim()); } catch { /* fall through */ }
  }
  const plainMatch = headerValue.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1].trim() : null;
};

interface ApiError extends Error {
  response?: { data: unknown; status: number };
  status?: number;
}

// The base URL is determined by the current domain
export const getBaseUrl = (): string => {
  // Check if we're running in a browser environment
  const isBrowser = typeof window !== 'undefined';

  if (isBrowser) {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;


    // Production environment with cloudflared - direct domains
    if (hostname.endsWith('.tomasek.dev')) {
      // In production with Cloudflare Tunnel, add the /api prefix
      // This creates URLs like hostname/api/accounts/api/login/
      return `${protocol}//${hostname}/api`;
    }

    // Development environment with tenant subdomains
    const isStockmanDomain = hostname.includes('stockman.local');
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (isLocalhost) {
      // webpack-dev-server proxy forwards /api to backend:8000
      return '/api';
    }

    if (isStockmanDomain) {
      if (window.location.port === '3366') {
        // Dev server on port 3366 — webpack proxy forwards /api to backend:8000
        return '/api';
      }

      // Production / nginx reverse proxy on same origin
      return `${protocol}//${hostname}/api`;
    }

    // Fallback - if we can't determine tenant, use relative URL
    return '/api';
  }

  // For server-side rendering or non-browser environments
  return 'http://koncertservis.stockman.local:8000/api';
};

// ---------------------------------------------------------------------------
// Responsive image thumbnails
// ---------------------------------------------------------------------------

/**
 * Extract the media-relative path from an image URL returned by DRF.
 * DRF's ImageField serialises to an absolute URL (https://host/media/foo.jpg)
 * or a relative path (/media/foo.jpg).  We need just the part after /media/.
 */
const _mediaPath = (imageUrl: string): string => {
  const pathname = imageUrl.startsWith('http') ? new URL(imageUrl).pathname : imageUrl;
  // Strip leading /media/ prefix
  return pathname.replace(/^\/media\//, '');
};

/**
 * Return a URL for a thumbnail of the given width.
 * URL format: /media/thumbnails/<width>/<media-relative-path>.jpg
 * In dev webpack proxies /media → backend; in prod nginx serves from disk or
 * falls through to Django for first-time generation.
 *
 * @param {string} imageUrl  - absolute or relative image URL from the API
 * @param {number} width     - desired width in pixels (will round up to nearest bucket)
 */
export const getThumbUrl = (imageUrl: string | null | undefined, width: number): string => {
  if (!imageUrl) return '';
  return `/media/thumbnails/${width}/${_mediaPath(imageUrl)}`;
};

/**
 * Build a `srcset` attribute string for an image.
 * @param {string} imageUrl  - absolute or relative image URL from the API
 * @param {number[]} widths  - widths to include in the srcset
 */
export const getThumbSrcSet = (imageUrl: string | null | undefined, widths: number[] = [400, 800, 1200]): string => {
  if (!imageUrl) return '';
  return widths.map((w) => `${getThumbUrl(imageUrl, w)} ${w}w`).join(', ');
};

/**
 * Function to create/get/renew CSRF token
 * Django expects a CSRF token for POST/PUT/DELETE requests
 */
export const ensureCsrfToken = async (): Promise<string | null> => {
  if (typeof document === 'undefined') return null;

  // Check if CSRF token exists in cookies
  const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }
    return null;
  };

  let csrfToken = getCsrfToken();

  // If no CSRF token, make a request to obtain it
  if (!csrfToken) {
    try {
      const response = await fetch(`${getBaseUrl()}/accounts/api/auth/csrf/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        // The response sets the cookie, so check again
        csrfToken = getCsrfToken();
      }
    } catch (error) {
      console.warn('Failed to obtain CSRF token:', error);
    }
  }

  return csrfToken;
};

/**
 * Generic fetch wrapper with error handling
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<any>}
 */
export const apiRequest = async <T = any>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> => {
  const url = `${getBaseUrl()}/${endpoint.replace(/^\//, '')}`;

  // Default headers
  const headers = new Headers(options.headers);

  // Only set Content-Type for non-FormData requests
  if (!(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  // Include CSRF token for state-changing requests.
  // The access token travels in an HttpOnly cookie (sent automatically by the
  // browser via credentials: 'include') so no Authorization header is needed.
  if (typeof document !== 'undefined' && options.method !== 'GET') {
    const csrfToken = await ensureCsrfToken();
    if (csrfToken) {
      headers.set('X-CSRFToken', csrfToken);
    }
  }

  // Add a retry mechanism for failed requests
  const MAX_RETRIES = 2;
  let retries = 0;

  const executeRequest = async () => {
    try {
      // If the access token is about to expire, proactively refresh it so the
      // next request succeeds.  We use the locally stored expiry timestamp as
      // a hint — the server is still the authoritative validator.
      if (authService && typeof authService.getTokenExpiry === 'function') {
        const expires = authService.getTokenExpiry();
        if (expires) {
          const now = new Date().getTime() / 1000;
          if (now >= expires - 300) {
            try {
              await authService.refreshToken();
            } catch (refreshError) {
              console.error('Proactive token refresh failed:', refreshError);
              authService.clearSession?.();
            }
          }
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
        // credentials: 'include' sends the HttpOnly oauth2_at cookie with
        // every request so the server can authenticate without an
        // Authorization header.
        credentials: 'include',
      });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        // Get detailed error message if available
        let errorDetails = '';
        try {
          const errorData = await response.clone().json();
          errorDetails = JSON.stringify(errorData);
        } catch (e) {
          // Ignore parsing errors
        }

        console.error(`Authentication error (${response.status}): ${errorDetails}`);

        // Clear local session metadata on auth errors
        authService?.clearSession?.();

        throw new Error(`Authentication required. Please log in. Details: ${errorDetails || response.statusText}`);
      }

      // Handle redirects which might indicate auth issues
      if (response.status === 302) {
        throw new Error('Session expired or authentication required.');
      }

      // Check if response is successful
      if (!response.ok) {
        // Try to get more detailed error information
        let errorMessage = `API error: ${response.status}`;
        let errorData = null;
        try {
          errorData = await response.clone().json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          } else {
            errorMessage = `API error: ${response.status} - ${JSON.stringify(errorData)}`;
          }
        } catch (e) {
          // If we can't parse the error as JSON, use the status text
          errorMessage = `API error: ${response.status} - ${response.statusText}`;
        }

        const err: ApiError = new Error(errorMessage);
        err.status = response.status;
        err.response = { status: response.status, data: errorData };
        throw err;
      }

      // For 204 No Content responses, return null
      if (response.status === 204) {
        return null;
      }

      // Check the responseType option to determine how to handle the response
      if (options.responseType === 'blob') {
        // Guard: if the server redirected to a login page, the response will be
        // text/html even though we expect binary data.  Treat it as an auth error
        // instead of silently returning an HTML blob that can't be opened.
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          if (authService && typeof authService.clearSession === 'function') {
            authService.clearSession();
          }
          throw new Error('Authentication required. Please log in.');
        }
        const blob = await response.blob();
        if (options.withFilename) {
          const filename = parseContentDispositionFilename(response.headers.get('content-disposition'));
          return { blob, filename } as T;
        }
        return blob as T; // Return response as a blob
      } else if (options.responseType === 'arraybuffer') {
        return response.arrayBuffer(); // Return response as an ArrayBuffer
      } else if (options.responseType === 'text') {
        return response.text(); // Return response as text
      } else {
        // Default: try to parse as JSON
        try {
          const contentType = response.headers.get('content-type');
          // If content type indicates a binary response
          if (contentType && contentType.includes('application/octet-stream')) {
            return response.blob();
          }

          // For JSON responses
          const text = await response.text();
          // If the response is empty or whitespace, return an empty object
          if (!text || !text.trim()) {
            return {};
          }

          // If it's a PDF response (starts with %PDF)
          if (text.startsWith('%PDF')) {
            const blob = new Blob([text], { type: 'application/pdf' });
            return blob;
          }

          // For base64 encoded data (like images)
          if (text.startsWith('JVB')) {
            return text; // Return the base64 data directly
          }

          try {
            const data = JSON.parse(text);
            // Unwrap DRF paginated responses: { count, results: [...] }
            if (!options.raw && data !== null && typeof data === 'object' && !Array.isArray(data) &&
                'results' in data && Array.isArray(data.results) && 'count' in data) {
              return data.results;
            }
            return data;
          } catch (jsonError) {
            // If not valid JSON but has content, return as text
            return text;
          }
        } catch (parseError) {
          console.error('Error parsing response:', parseError);

          // Try to get the raw response
          try {
            const rawResponse = await response.clone().text();

            // For base64 encoded data
            if (rawResponse.startsWith('JVB')) {
              return rawResponse;
            }

            return rawResponse;
          } catch (e) {
            throw new Error(`Failed to parse API response: ${parseError.message}`);
          }
        }
      }
    } catch (error) {
      // If we still have retries left and this isn't already a retry
      if (retries < MAX_RETRIES) {
        retries++;
                // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        return executeRequest();
      }

      throw error;
    }
  };

  try {
    return await executeRequest();
  } catch (error) {
    console.error(`API request failed for ${url}:`, error);
    throw error;
  }
};

// Convenience methods for common HTTP operations
export const api = {
  getBaseUrl,

  get: <T = any>(endpoint: string, options: ApiRequestOptions = {}) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T = any>(endpoint: string, data: unknown, options: ApiRequestOptions = {}) => {
    // Don't stringify FormData objects
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body,
    });
  },

  put: <T = any>(endpoint: string, data: unknown, options: ApiRequestOptions = {}) => {
    // Don't stringify FormData objects
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body,
    });
  },

  patch: <T = any>(endpoint: string, data: unknown, options: ApiRequestOptions = {}) => {
    // Don't stringify FormData objects
    const body = data instanceof FormData ? data : JSON.stringify(data);
    return apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body,
    });
  },

  delete: (endpoint, options = {}) =>
    apiRequest(endpoint, { ...options, method: 'DELETE' }),

  /**
   * GET without unwrapping paginated responses — returns raw { count, results, next, previous }.
   */
  getRaw: (endpoint, options = {}) =>
    apiRequest(endpoint, { ...options, method: 'GET', raw: true }),

  /**
   * Fetch ALL pages of a paginated DRF endpoint, returning a flat array.
   * @param {string} endpoint  - base endpoint (may already contain query params)
   * @param {number} pageSize  - items per page (default 100)
   */
  fetchAll: async (endpoint, pageSize = 100) => {
    const allResults = [];
    let page = 1;
    const sep = endpoint.includes('?') ? '&' : '?';
    while (true) {
      const results = await apiRequest(
        `${endpoint}${sep}page=${page}&page_size=${pageSize}`,
        { method: 'GET' }
      );
      const arr = Array.isArray(results) ? results : [];
      if (arr.length === 0) break;
      allResults.push(...arr);
      if (arr.length < pageSize) break; // last page
      page++;
    }
    return allResults;
  },

  // Add a convenience method for file uploads with progress tracking
  uploadWithProgress: (endpoint, formData, onProgress) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${getBaseUrl()}/${endpoint.replace(/^\//, '')}`;

      xhr.open('POST', url);

      // Include credentials (cookies)
      xhr.withCredentials = true;

      // Do NOT set Content-Type header for FormData
      // The browser will set it with the correct boundary

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      };

      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
            resolve(response);
          } catch (e) {
            resolve(xhr.responseText);
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Network error during upload'));
      };

      xhr.send(formData);
    });
  }
};
